<script lang="ts">
    import PaypalButtons from "./PaypalButtons.svelte"
    import KlarnaButtons from "./KlarnaButtons.svelte"
    import { setUser } from "../../../sentry"
    import { maxInputLength } from "../../../../../api/hooks/config-client"
    import { spaNavigate } from "../../../functionTools/navigation"
    import { login } from "../../../functionTools/login"
    import { cart, checkoutData, getAsyncCartSum } from "../../../functionTools/cart"
    import { overlay } from "../../../store"
    import { location } from "../../../i18n"
    import { _ } from "svelte-i18n"
    import { getCountry, postOrder, errorCodeFrom, errorMessageKeyFrom } from "../../../api"
    import { purchaseFromOrder } from "../../../lib/ga"
    import CheckoutErrorBox from "./CheckoutErrorBox.svelte"
    import { truncateFields } from "./helper"
    import Cart from "../../ecommerce/cart/Cart.svelte"
    import FormInputText from "../../widgets/Form/FormInputText.svelte"
    import FormInputCheckbox from "../../widgets/Form/FormInputCheckbox.svelte"
    import CheckoutFooter from "./CheckoutFooter.svelte"
    import BarLoader from "svelte-loading-spinners/BarLoader.svelte"
    import Checkboxes from "./Checkboxes.svelte"

    let cancellationNotice = $state(false)
    let privacyNotice = $state(false)
    let conditionsNotice = $state(false)
    let loading = $state(false)
    let errorCode: string | null = $state(null)
    let errorKey = $state("checkout.error-order")

    location.subscribe((l) => {
        if (l?.path == "/checkout/overview") {
            $overlay.show = false
        }
    })

    let paymentName: string = $derived(
        (() => {
            switch ($checkoutData.payment.type) {
                case "paypal":
                    return "Paypal"
                case "invoice":
                    return $_("checkout.invoice")
            }
        })()
    )

    let deliveryCountry: Country = $state()
    let cartSum: CartSum = $state()

    async function _getCartSum(_cart: CartItem[]) {
        deliveryCountry = await getCountry($checkoutData.deliveryAddress.countryCode)
        cartSum = await getAsyncCartSum(_cart, deliveryCountry)
    }

    const submitOrder = async () => {
        try {
            $checkoutData.contact.email = $checkoutData.contact.email.trim()
            setUser({
                email: $checkoutData.contact.email,
            })

            $checkoutData.contact = truncateFields($checkoutData.contact, maxInputLength)
            $checkoutData.deliveryAddress = truncateFields($checkoutData.deliveryAddress, maxInputLength)
            $checkoutData.billingAddress = truncateFields($checkoutData.billingAddress, maxInputLength)

            // Bei kostenlosem Warenkorb setze Payment auf "invoice" (Rechnung)
            const payment: Payment = cartSum?.totalGross === 0 ? { type: "invoice" } : $checkoutData.payment

            const orderResult = await postOrder({
                contact: $checkoutData.contact,
                deliveryAddress: $checkoutData.deliveryAddress,
                deliveryCountry: deliveryCountry,
                billingAddress: $checkoutData.billingAddress,
                sameAddress: $checkoutData.sameAddress,
                vat: $checkoutData.vat,
                sum: cartSum,
                payment: payment,
                cart: $cart,
            })

            // Feedback-Token für Kauferfahrung-Bewertung speichern
            if (typeof window !== "undefined" && orderResult?._feedbackToken) {
                window.sessionStorage.setItem("feedbackToken", orderResult._feedbackToken)
            }

            // GA4 purchase in die Reload-sichere Queue (feuert auf Confirmation, nur mit Consent)
            purchaseFromOrder(orderResult)

            typeof window !== "undefined" &&
                window.setTimeout(() => {
                    $checkoutData.stepsValid = 0
                    $cart = []
                    $checkoutData.payment = { type: $checkoutData.payment.type }
                }, 1000)

            spaNavigate("/checkout/confirmation")
        } catch (e) {
            console.log(e)
            errorCode = errorCodeFrom(e)
            errorKey = errorMessageKeyFrom(e)
            loading = false
            // Bewusst kein Re-Throw: Die Box ist die Fehlermeldung. Ein Re-Throw
            // würde als unbehandelte Rejection/Pageerror enden (Test-Monitor),
            // ohne dem Kunden zu helfen. Der Code steht in errorCode.
            return
        }
    }

    function submitClick() {
        if (!loading) {
            loading = true
            return submitOrder()
        }
        return Promise.resolve()
    }
    $effect(() => {
        if (!$cart?.length || $login) spaNavigate("/", { replace: true })
        else if ($checkoutData.stepsValid < 1) spaNavigate("/checkout", { replace: true })
        // Bei kostenlosem Warenkorb wird Payment übersprungen, daher nur stepsValid >= 1 erforderlich
        else if (cartSum?.totalGross > 0 && $checkoutData.stepsValid < 2)
            spaNavigate("/checkout/payment", { replace: true })
        // Nur bei kostenpflichtigem Warenkorb Payment-Type erforderlich
        if (cartSum?.totalGross > 0 && !$checkoutData.payment.type) spaNavigate("/checkout/payment", { replace: true })
    })
    $effect(() => {
        _getCartSum($cart)
    })

    $effect(() => {
        if (cartSum?.totalGross > 500 && $checkoutData.payment.type == "invoice")
            spaNavigate("/checkout/payment", { replace: true })
    })
</script>

<div class="subline">{$_("checkout.order")}</div>
<h2 class="h2_nooffset">{$_("checkout.summary")}</h2>

<CheckoutErrorBox code={errorCode} messageKey={errorKey} />

<form
    onsubmit={(e) => {
        e.preventDefault()
        submitClick()
    }}
>
    <section>
        <div class="section_headline">{$_("checkout.cart")}</div>
        <Cart checkoutButton={false} />
    </section>
    <section>
        <div class="section_headline">{$_("checkout.contact-data")}</div>
        <div class="row">
            <div class="col-md-12">
                <FormInputText name="email" label={$_("checkout.email")} disabled value={$checkoutData.contact.email} />
            </div>
            <div class="col-md-12">
                <FormInputText name="phone" label={$_("checkout.phone")} disabled value={$checkoutData.contact.phone} />
            </div>
        </div>
    </section>
    <section>
        <div class="section_headline">{$_("checkout.shipping-address")}</div>
        <div class="row">
            <div class="col-md-6">
                <FormInputText
                    name="delivery_firstname"
                    label={$_("checkout.firstname")}
                    disabled
                    value={$checkoutData.deliveryAddress.firstname}
                />
            </div>
            <div class="col-md-6">
                <FormInputText
                    name="delivery_lastname"
                    label={$_("checkout.lastname")}
                    disabled
                    value={$checkoutData.deliveryAddress.lastname}
                />
            </div>
            <div class="col-md-10">
                <FormInputText
                    name="delivery_street"
                    label={$_("checkout.street")}
                    disabled
                    value={$checkoutData.deliveryAddress.street}
                />
            </div>
            <div class="col-md-2">
                <FormInputText
                    name="delivery_houseNumber"
                    placeholder={$_("checkout.house-number")}
                    label={$_("checkout.house-number")}
                    disabled
                    value={$checkoutData.deliveryAddress.houseNumber}
                />
            </div>
            <div class="col-md-6">
                <FormInputText
                    name="delivery_postcode"
                    placeholder={$_("checkout.postcode")}
                    label={$_("checkout.postcode")}
                    disabled
                    value={$checkoutData.deliveryAddress.postcode}
                />
            </div>
            <div class="col-md-6">
                <FormInputText
                    name="delivery_city"
                    placeholder={$_("checkout.city")}
                    label={$_("checkout.city")}
                    disabled
                    value={$checkoutData.deliveryAddress.city}
                />
            </div>
            <div class="col-md-12">
                <FormInputText
                    name="delivery_country"
                    placeholder={$_("checkout.country")}
                    label={$_("checkout.country")}
                    disabled
                    value={$checkoutData.deliveryAddress.countryCode}
                />
            </div>
        </div>
    </section>

    {#if cartSum?.totalGross > 0}
        <section>
            <div class="section_headline">{$_("checkout.billing-address")}</div>
            <div class="row">
                <div class="col-md-6">
                    <FormInputText
                        name="billing_firstname"
                        placeholder={$_("checkout.firstname")}
                        label={$_("checkout.firstname")}
                        disabled
                        value={$checkoutData.billingAddress.firstname}
                    />
                </div>
                <div class="col-md-6">
                    <FormInputText
                        name="billing_lastname"
                        placeholder={$_("checkout.lastname")}
                        label={$_("checkout.lastname")}
                        disabled
                        value={$checkoutData.billingAddress.lastname}
                    />
                </div>
                <div class="col-md-10">
                    <FormInputText
                        name="billing_street"
                        placeholder={$_("checkout.street")}
                        label={$_("checkout.street")}
                        disabled
                        value={$checkoutData.billingAddress.street}
                    />
                </div>
                <div class="col-md-2">
                    <FormInputText
                        name="billing_houseNumber"
                        placeholder={$_("checkout.house-number")}
                        label={$_("checkout.house-number")}
                        disabled
                        value={$checkoutData.billingAddress.houseNumber}
                    />
                </div>
                <div class="col-md-6">
                    <FormInputText
                        name="billing_postcode"
                        placeholder={$_("checkout.postcode")}
                        label={$_("checkout.postcode")}
                        disabled
                        value={$checkoutData.billingAddress.postcode}
                    />
                </div>
                <div class="col-md-6">
                    <FormInputText
                        name="billing_city"
                        placeholder={$_("checkout.city")}
                        label={$_("checkout.city")}
                        disabled
                        value={$checkoutData.billingAddress.city}
                    />
                </div>
                <div class="col-md-12">
                    <FormInputText
                        name="billing_country"
                        placeholder={$_("checkout.country")}
                        label={$_("checkout.country")}
                        disabled
                        value={$checkoutData.billingAddress.countryCode}
                    />
                </div>
            </div>
        </section>
    {/if}

    {#if cartSum?.totalGross > 0}
        <section style="border-bottom:dotted 1px var(--border-color-2);">
            <div class="section_headline">{$_("checkout.payment")}</div>
            <p>{paymentName}</p>
        </section>
    {/if}

    <section>
        <FormInputCheckbox label={$_("checkout.store-my-data")} bind:checked={$checkoutData.localStorage} />
        <Checkboxes bind:cancellationNotice bind:privacyNotice bind:conditionsNotice />
    </section>
    {#if loading}
        <BarLoader />
    {:else}
        <CheckoutFooter
            backlink={cartSum?.totalGross === 0 ? "/checkout" : "/checkout/payment"}
            backtitle={cartSum?.totalGross === 0 ? $_("checkout.back-to-shipping") : $_("checkout.back-to-payment")}
            nexttitle={cartSum?.totalGross === 0 ? $_("checkout.order-now-free") : $_("checkout.order-now")}
            NextComponent={cartSum?.totalGross === 0
                ? null
                : { paypal: PaypalButtons, klarna: KlarnaButtons, invoice: null }[$checkoutData.payment.type]}
            submitDisabled={!cancellationNotice || !privacyNotice || !conditionsNotice || !deliveryCountry || !cartSum}
            submitHandler={submitClick}
        />
    {/if}
</form>
